import { IIntegrationHealthItem, IntegrationHealthStatus, IWebhookHealthWarning } from '../../models/IntegrationHealth';

// Failing first — the broken connection is the reason this widget exists — then rows whose last import or sync
// failed (the connection checks out but data isn't moving), then healthy rows whose saved settings have gone stale
// (working connection, quietly broken configuration), then never-checked, then working, with unverifiable last
// (nothing on it is actionable from here).
const STATUS_ORDER: Record<IntegrationHealthStatus, number> = {
    failing: 0,
    unknown: 2,
    healthy: 3,
    unverifiable: 4,
};

const SYNC_FAILURE_RANK = 0.25;

export const rankOf = (item: IIntegrationHealthItem): number =>
    item.status === 'failing'
        ? STATUS_ORDER.failing
        : (item.syncFailures?.length ?? 0) > 0
            ? SYNC_FAILURE_RANK
            : (item.staleOptionValues?.length ?? 0) > 0
                ? 1
                : STATUS_ORDER[item.status] ?? 9;

// A failing webhook (AC-1203) ranks just after failing integrations: something is broken, but it's an outbound
// call rather than the catalog's connection.
const WEBHOOK_WARNING_RANK = 0.5;

/** One row of the widget: an installed integration, or a webhook whose last call failed. */
export type HealthRow =
    | { kind: 'integration', key: string, rank: number, name: string, item: IIntegrationHealthItem }
    | { kind: 'webhook', key: string, rank: number, name: string, warning: IWebhookHealthWarning };

/** Integrations and failing webhooks as one list, most actionable first, then by name. */
export const buildHealthRows = (items: IIntegrationHealthItem[], webhookWarnings: IWebhookHealthWarning[]): HealthRow[] => [
    ...items.map((item): HealthRow => ({
        kind: 'integration', key: `i:${item.installedId}`, rank: rankOf(item), name: item.name ?? '', item,
    })),
    ...webhookWarnings.map((warning): HealthRow => ({
        kind: 'webhook', key: `w:${warning.webhookId}`, rank: WEBHOOK_WARNING_RANK, name: warning.name ?? '', warning,
    })),
].sort((a, b) => a.rank - b.rank || a.name.localeCompare(b.name));
